export const LUNAVERSE_AGENT_SETTINGS = {
    provider: "lunaverse.agent.provider",
    baseUrl: "lunaverse.agent.baseUrl",
    model: "lunaverse.agent.model",
    apiKey: "lunaverse.agent.apiKey",
    useIdeGateway: "lunaverse.agent.useIdeGateway",
    codexPath: "lunaverse.agent.codexPath"
};
export const LUNAVERSE_AGENT_ENV = {
    provider: "LUNAVERSE_AGENT_PROVIDER",
    baseUrl: "LUNAVERSE_AGENT_BASE_URL",
    model: "LUNAVERSE_AGENT_MODEL",
    apiKey: "LUNAVERSE_AGENT_API_KEY",
    codexPath: "LUNAVERSE_AGENT_CODEX_PATH"
};
export const LUNAVERSE_GENERATION_API_ENV = {
    mobAiKey: "MOB_AI_KEY",
    legacyMobAiApiKey: "MOB_AI_API_KEY"
};
export const DIRECT_MODEL_API_KEY_ENV = {
    deepseek: "DEEPSEEK_API_KEY"
};
export const R2_STORAGE_SETTINGS = {
    endpoint: "lunaverse.storage.r2.endpoint",
    bucket: "lunaverse.storage.r2.bucket",
    accessKeyId: "lunaverse.storage.r2.accessKeyId",
    secretAccessKey: "lunaverse.storage.r2.secretAccessKey",
    publicBase: "lunaverse.storage.r2.publicBase",
    region: "lunaverse.storage.r2.region"
};
export const R2_STORAGE_ENV = {
    endpoint: "R2_ENDPOINT",
    bucket: "R2_BUCKET",
    accessKeyId: "R2_ACCESS_KEY_ID",
    secretAccessKey: "R2_SECRET_ACCESS_KEY",
    publicBase: "R2_PUBLIC_BASE",
    region: "R2_REGION"
};
export const REQUIRED_R2_STORAGE_ENV = [
    R2_STORAGE_ENV.endpoint,
    R2_STORAGE_ENV.bucket,
    R2_STORAGE_ENV.accessKeyId,
    R2_STORAGE_ENV.secretAccessKey,
    R2_STORAGE_ENV.publicBase
];
function stringValue(value) {
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed || undefined;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return String(value);
    }
    if (typeof value === "boolean") {
        return String(value);
    }
    return undefined;
}
function pickSetting(getSetting, key) {
    return stringValue(getSetting?.(key));
}
const LUNAVERSE_API_KEY_ENV_FALLBACKS = [
    LUNAVERSE_AGENT_ENV.apiKey,
    LUNAVERSE_GENERATION_API_ENV.mobAiKey,
    LUNAVERSE_GENERATION_API_ENV.legacyMobAiApiKey
];
const DIRECT_MODEL_API_KEY_ENV_FALLBACKS = [DIRECT_MODEL_API_KEY_ENV.deepseek];
export function resolveLunaverseApiKey(sources = {}) {
    const settingKey = pickSetting(sources.getSetting, LUNAVERSE_AGENT_SETTINGS.apiKey);
    if (settingKey)
        return settingKey;
    const envKeys = sources.includeDirectModelFallbacks
        ? [...LUNAVERSE_API_KEY_ENV_FALLBACKS, ...DIRECT_MODEL_API_KEY_ENV_FALLBACKS]
        : LUNAVERSE_API_KEY_ENV_FALLBACKS;
    for (const key of envKeys) {
        const value = stringValue(sources.env?.[key]) ?? stringValue(sources.dotEnv?.[key]);
        if (value)
            return value;
    }
    return stringValue(sources.defaults?.apiKey);
}
export function buildLunaverseGenerationEnv(input, resolvedApiKey) {
    const env = { ...(input ?? {}) };
    const apiKey = stringValue(resolvedApiKey) ?? resolveLunaverseApiKey({ env });
    if (apiKey) {
        env[LUNAVERSE_GENERATION_API_ENV.mobAiKey] = apiKey;
        env[LUNAVERSE_AGENT_ENV.apiKey] = apiKey;
    }
    delete env[LUNAVERSE_GENERATION_API_ENV.legacyMobAiApiKey];
    return env;
}
export function lunaverseApiKeyMissingMessage(action) {
    // Shown only in agent/runtime error context (assetctl failures), where no
    // AppLocale is plumbed through. Emit both languages so either audience can read it.
    const en = `${action} is missing the Lunaverse API Key. Configure it in user settings ${LUNAVERSE_AGENT_SETTINGS.apiKey}; the legacy env vars still work, but the new setting is all you need.`;
    const zh = `${action}缺少 Lunaverse API Key。请在用户设置 ${LUNAVERSE_AGENT_SETTINGS.apiKey} 中配置；旧环境变量仍兼容，但新配置只需要这一项。`;
    return `${en} / ${zh}`;
}
function setFromSettingOrEnv(out, env, getSetting, settingKey, envKey) {
    const value = pickSetting(getSetting, settingKey) ?? stringValue(env[envKey]);
    if (value) {
        out[envKey] = value;
    }
    else {
        delete out[envKey];
    }
}
export function buildR2StorageEnv(input = {}, getSetting) {
    const out = { ...input };
    setFromSettingOrEnv(out, input, getSetting, R2_STORAGE_SETTINGS.endpoint, R2_STORAGE_ENV.endpoint);
    setFromSettingOrEnv(out, input, getSetting, R2_STORAGE_SETTINGS.bucket, R2_STORAGE_ENV.bucket);
    setFromSettingOrEnv(out, input, getSetting, R2_STORAGE_SETTINGS.accessKeyId, R2_STORAGE_ENV.accessKeyId);
    setFromSettingOrEnv(out, input, getSetting, R2_STORAGE_SETTINGS.secretAccessKey, R2_STORAGE_ENV.secretAccessKey);
    setFromSettingOrEnv(out, input, getSetting, R2_STORAGE_SETTINGS.publicBase, R2_STORAGE_ENV.publicBase);
    setFromSettingOrEnv(out, input, getSetting, R2_STORAGE_SETTINGS.region, R2_STORAGE_ENV.region);
    return out;
}
export function missingR2StorageEnv(env) {
    return REQUIRED_R2_STORAGE_ENV.filter((key) => !stringValue(env[key]));
}
